"use client";

import { useState } from "react";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui";
import { useUser } from "@/contexts/user-provider";
import { useLocations } from "@/hooks/use-locations";
import { toast } from "@/lib/use-toast";
import { useAppointmentLocationPromptStore } from "@/stores/appointment-location-prompt-store";

/**
 * Offers to move the user to the location of the appointment they are
 * charting or charging for, when it differs from the location they have
 * selected. Opens on its own; closing it keeps the user where they are.
 * Shows nothing for a location the user can't work at, or when there is only
 * one location to be at.
 */
export default function AppointmentLocationPrompt({
  locationId,
  appointmentId
}: {
  /** Where the associated appointment takes place. */
  locationId?: string | null;
  /**
   * The appointment itself, when known. "Stay" is then remembered for it
   * across screens, so the note editor doesn't re-ask what the dialog that
   * started the note just did.
   */
  appointmentId?: string | null;
}) {
  const {
    currentLocationId,
    updatePrimaryLocation
  } = useUser();
  const {
    locations
  } = useLocations();
  // Answered (stayed or switched) for this location: a later change of the
  // user's own location shouldn't bring the question back.
  const [dismissedLocationId, setDismissedLocationId] = useState<string>();
  const [isSwitching, setIsSwitching] = useState(false);
  const isDeclinedAppointment = useAppointmentLocationPromptStore(state => !!appointmentId && !!state.declinedAppointmentIds[appointmentId]);
  const declineAppointment = useAppointmentLocationPromptStore(state_0 => state_0.declineAppointment);
  const appointmentLocation = locationId ? locations.find(location => location.id === locationId) : undefined;
  const isOpen = !!appointmentLocation && locations.length >= 2 && appointmentLocation.id !== currentLocationId && appointmentLocation.id !== dismissedLocationId && !isDeclinedAppointment;
  if (!appointmentLocation) return null;
  const currentLocationName = locations.find(location_0 => location_0.id === currentLocationId)?.name ?? "your current location";
  // With no location selected the user sees every location, which isn't a
  // place to "work at" or "stay at".
  const whereYouAre = currentLocationId ? `working at ${currentLocationName}` : "viewing all locations";
  const stayLabel = currentLocationId ? `Stay at ${currentLocationName}` : "Keep all locations";
  const dismiss = () => {
    setDismissedLocationId(appointmentLocation.id);
    if (appointmentId) declineAppointment(appointmentId);
  };
  const handleSwitch = async () => {
    setIsSwitching(true);
    let switched = false;
    try {
      switched = await updatePrimaryLocation(appointmentLocation.id);
    } catch (error) {
      console.error("Failed to switch location:", error);
    } finally {
      setIsSwitching(false);
    }
    if (switched) {
      setDismissedLocationId(appointmentLocation.id);
    } else {
      toast({
        title: "Couldn't switch location",
        description: `You're still ${whereYouAre}. Try again, or change it from your profile menu.`,
        variant: "error"
      });
    }
  };
  return <Dialog open={isOpen} onOpenChange={open => {
    if (!open && !isSwitching) dismiss();
  }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            This appointment is at {appointmentLocation.name}
          </DialogTitle>
          <DialogDescription>
            You&apos;re {whereYouAre}. Switch to {appointmentLocation.name}?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={dismiss} disabled={isSwitching}>
            {stayLabel}
          </Button>
          <Button type="button" variant="primary" onClick={handleSwitch} isLoading={isSwitching}>
            Switch to {appointmentLocation.name}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}